import { ChangeEvent, FC, useEffect, useState } from 'react';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { FiChevronRight } from 'react-icons/fi';
import { useNavigate } from 'react-router';
import Swal from 'sweetalert2';
import { StyledInput } from '../../components/StyledInput';
import { Loading } from '../../components/Loading';
import { StyledButton } from '../../components/StyledButton/index';
import {
  completePasswordReset,
  forgotPassword,
  signIn,
} from '../../hooks/useAuth';
import theme from '../../styles/themes/default';
import { getCookie } from '../../utils/CookieHelper';
import { emailValidation } from '../../utils/EmailValidation';
import {
  LabelTop,
  LeftSideLogoContainer,
  LoginContainer,
  LogoContainerMobileScreen,
  RightSideFormLoginContainer,
} from './styles';
import { useUser } from '../../context/UserContext';

const LoginAws: FC = () => {
  const { user, updateUserContextState } = useUser();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: '',
    password: '',
  });
  const [isFormValid, setIsFormValid] = useState({
    emailValidated: false,
    passwordValidated: false,
  });
  const [resetForm, setResetForm] = useState({
    email: '',
    password: '',
    verificationCode: '',
  });
  const [isResetFormValid, setIsResetFormValid] = useState({
    emailValidated: false,
    passwordValidated: false,
    verificationCode: false,
  });
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [askEmailToResetPassword, setAskEmailToResetPassword] = useState(false);
  const [isFormToResetPassword, setIsFormToResetPassword] = useState(false);

  const [loading, setLoading] = useState(false);

  const handleLoginInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === 'email') {
      const emailValidated = emailValidation({
        email: value,
        emptyEmail: 'Campo obrigatório',
        incorrectEmail: 'Email inválido',
      });

      emailValidated.message === ''
        ? setIsFormValid((prev) => ({ ...prev, emailValidated: true }))
        : setIsFormValid((prev) => ({ ...prev, emailValidated: false }));
    }

    if (name === 'password') {
      const passwordValidated =
        /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[A-Z])(?=.*[a-z]).*$/.test(value);
      value.length >= 8 && passwordValidated
        ? setIsFormValid((prev) => ({ ...prev, passwordValidated: true }))
        : setIsFormValid((prev) => ({ ...prev, passwordValidated: false }));
    }

    setForm({ ...form, [name]: value });
  };

  const handleLoginSubmit = async (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    try {
      const userSignInData = await signIn(form);

      updateUserContextState(
        userSignInData.userData,
        userSignInData.cognitoResult,
      );

      navigate('/');
    } catch (error: any) {
      setLoading(false);
      const message = error.response?.data?.message.includes(
        'Username/client id combination not found',
      )
        ? 'Email invalido ou não cadastrado!'
        : error.response?.data?.message.includes(
            'Incorrect username or password',
          )
        ? 'Email ou senha incorretos!'
        : error.response?.data?.message.includes('Attempt limit exceeded')
        ? 'Limite de tentativas excedido!'
        : error.response?.data?.message.includes('User is not confirmed')
        ? 'Usuário não confirmado. Verifique novamente seu e-mail.'
        : error.response?.data?.message;

      Swal.fire({
        icon: 'error',
        html: message,
        confirmButtonColor: theme.colors.orange.primary,
      });
    }
  };

  const handleResetFormInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === 'verificationCode') {
      const valueFormatted = value.replace(/[^0-9]/g, '').trim();
      valueFormatted.length > 5
        ? setIsResetFormValid((prev) => ({ ...prev, verificationCode: true }))
        : setIsResetFormValid((prev) => ({ ...prev, verificationCode: false }));

      setResetForm({
        ...resetForm,
        verificationCode: valueFormatted,
      });
      return;
    }

    if (name === 'email') {
      const emailValidated = emailValidation({
        email: value,
        emptyEmail: 'Campo obrigatório',
        incorrectEmail: 'Email inválido',
      });

      emailValidated.message === ''
        ? setIsResetFormValid((prev) => ({ ...prev, emailValidated: true }))
        : setIsResetFormValid((prev) => ({ ...prev, emailValidated: false }));
    }

    if (name === 'password') {
      const passwordValidated =
        /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[A-Z])(?=.*[a-z]).*$/.test(value);
      value.length >= 8 && passwordValidated
        ? setIsResetFormValid((prev) => ({ ...prev, passwordValidated: true }))
        : setIsResetFormValid((prev) => ({
            ...prev,
            passwordValidated: false,
          }));
    }

    setResetForm({ ...resetForm, [name]: value });
  };

  const handleResetPasswordSubmit = async (
    event: ChangeEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    try {
      const result = await completePasswordReset(resetForm);

      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        html: `Senha Atualizada com sucesso!`,
        confirmButtonColor: theme.colors.orange.primary,
      }).then(async () => {
        if (result.statusCode === 201) {
          const signInResult = await signIn({
            email: resetForm.email,
            password: resetForm.password,
          });

          updateUserContextState(
            signInResult.userData,
            signInResult.cognitoResult,
          );
        }
      });

      navigate('/');
    } catch (error: any) {
      const message = error.response?.data?.message.includes(
        'Username/client id combination not found',
      )
        ? 'Email invalido ou não cadastrado!'
        : error.response?.data?.message.includes(
            'Incorrect username or password',
          )
        ? 'Email ou senha incorretos!'
        : error.response?.data?.message.includes('Attempt limit exceeded')
        ? 'Limite de tentativas excedido!'
        : error.response?.data?.message.includes('User is not confirmed')
        ? 'Usuário não confirmado. Verifique novamente seu e-mail.'
        : error.response?.data?.message.includes(
            'Invalid verification code provided',
          )
        ? 'Código inválido. Verifique novamente seu e-mail.'
        : error.response?.data?.message;

      Swal.fire({
        icon: 'error',
        html: message,
        confirmButtonColor: theme.colors.orange.primary,
      });
    }
  };

  const handleEmailLinkToResetPassword = async (
    event: ChangeEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    try {
      const result = await forgotPassword(event.target.email.value);

      setIsFormToResetPassword(true);
    } catch (error: any) {
      const message = error.response?.data?.message.includes(
        'Username/client id combination not found',
      )
        ? 'Email invalido ou não cadastrado!'
        : error.response?.data?.message;
      Swal.fire({
        grow: 'row',
        icon: 'error',
        title: 'Informações inconsistentes!',
        html: message,
        confirmButtonColor: theme.colors.orange.primary,
      });
    }
  };

  useEffect(() => {
    console.log(user)
    if (user && Object.keys(user).length > 0){
      navigate(getCookie('lastPath') || '/');
    }
  }, [user]);

  return (
    <LoginContainer>
      {loading && <Loading />}
      <LeftSideLogoContainer>
        <div>
          <img src="/logos/zigauto-white-logo.png" alt="ZIGNET LOGO" />
          <h1>FROTAS</h1>
        </div>
      </LeftSideLogoContainer>
      <RightSideFormLoginContainer>
        <LogoContainerMobileScreen>
          <img src="/logos/zigauto-white-logo.png" alt="ZIGNET LOGO" />
          <h1>FROTAS</h1>
        </LogoContainerMobileScreen>

        {isFormToResetPassword ? (
          <form onSubmit={handleResetPasswordSubmit}>
            <h3>
              Foi enviado um Código para seu E-mail. <br />
              Favor informar e definir uma nova senha
            </h3>
            <LabelTop>
              <p>Código</p>
              <StyledInput
                type="text"
                name="verificationCode"
                placeholder="Código de verificação"
                onChange={handleResetFormInputChange}
                value={resetForm.verificationCode}
                maxLength={6}
                required
              />
            </LabelTop>
            <LabelTop>
              <p>E-mail</p>
              <StyledInput
                type="email"
                name="email"
                placeholder="E-mail"
                onChange={handleResetFormInputChange}
                value={resetForm.email}
                error={
                  !isResetFormValid.emailValidated && resetForm.email.length > 3
                }
                errorMessage="Favor informar um E-mail válido"
                required
              />
            </LabelTop>
            <LabelTop>
              <p>Nova Senha</p>
              <StyledInput
                type={isPasswordVisible ? 'text' : 'password'}
                name="password"
                placeholder="Senha"
                autoComplete="off"
                onChange={handleResetFormInputChange}
                value={resetForm.password}
                error={
                  !isResetFormValid.passwordValidated &&
                  resetForm.password.length > 3
                }
                errorMessage="Senha inválida - Mínimo 8 caracteres e com os critérios de segurança"
                required
              ></StyledInput>
              {isPasswordVisible ? (
                <AiOutlineEyeInvisible
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                />
              ) : (
                <AiOutlineEye
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                />
              )}
            </LabelTop>

            <StyledButton
              type="submit"
              text={'Confirmar'}
              icon={<FiChevronRight />}
              iconPosition="right"
              iconWhiteBackground={true}
              iconBorderRadius={true}
              disabled={
                isResetFormValid.emailValidated &&
                isResetFormValid.passwordValidated &&
                isResetFormValid.verificationCode
                  ? false
                  : true
              }
            />
          </form>
        ) : askEmailToResetPassword ? (
          <form onSubmit={handleEmailLinkToResetPassword}>
            <h3>
              Favor informar seu E-mail <br />
              para Redefinir sua senha
            </h3>
            <LabelTop>
              <p>E-mail</p>
              <StyledInput
                type="email"
                name="email"
                placeholder="E-mail"
                onChange={handleLoginInputChange}
                value={form.email}
                error={!isFormValid.emailValidated && form.email.length > 3}
                errorMessage="Favor informar um E-mail válido"
                required
              />
            </LabelTop>
            <StyledButton
              type="submit"
              text={'Confirmar'}
              icon={<FiChevronRight />}
              iconPosition="right"
              iconWhiteBackground={true}
              iconBorderRadius={true}
              disabled={isFormValid.emailValidated ? false : true}
            />
          </form>
        ) : (
          <form onSubmit={handleLoginSubmit}>
            <h1>Faça o seu login</h1>
            <LabelTop>
              <p>E-mail</p>
              <StyledInput
                type="email"
                name="email"
                placeholder="E-mail"
                onChange={handleLoginInputChange}
                value={form.email}
                error={!isFormValid.emailValidated && form.email.length > 3}
                errorMessage="Favor informar um E-mail válido"
                required
              />
            </LabelTop>
            <LabelTop>
              <p>Senha</p>
              <StyledInput
                type={isPasswordVisible ? 'text' : 'password'}
                name="password"
                placeholder="Senha"
                onChange={handleLoginInputChange}
                value={form.password}
                error={
                  !isFormValid.passwordValidated && form.password.length > 3
                }
                errorMessage="Senha inválida - Mínimo 8 caracteres e com os critérios de segurança"
                required
              />
              {isPasswordVisible ? (
                <AiOutlineEyeInvisible
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                />
              ) : (
                <AiOutlineEye
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                />
              )}
            </LabelTop>

            <span onClick={() => setAskEmailToResetPassword(true)}>
              Esqueci minha senha
            </span>

            <StyledButton
              type="submit"
              text="Entrar"
              icon={<FiChevronRight />}
              iconPosition="right"
              iconWhiteBackground={true}
              iconBorderRadius={true}
              textColor="#fff"
              disabled={
                isFormValid.emailValidated && isFormValid.passwordValidated
                  ? false
                  : true
              }
            />
          </form>
        )}
      </RightSideFormLoginContainer>
    </LoginContainer>
  );
};

export default LoginAws;
